/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColDef, GetRowIdParams, GridReadyEvent, RowSelectedEvent } from '@ag-grid-community/core';
import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AgFns,
  GridOptionsExt,
  ISortModel,
  ProxAgFns,
  RowSelectionMultipleCheckbox,
  SaveAndQueryComponent,
  StatusService,
  wrapBusy,
} from '@core';
import { BaseService } from '@core';
import { UtilFns } from '@utils';
import * as _ from 'lodash-es';
import { EntityFns } from '@data';
import { EntityError } from 'breeze-client';
import {
  ProgramProductTypeTag,
  AccountUser,
  ShippingUserGroup,
  ActiveStatus,
  ActiveStatusEnum,
  ShippingUserGroupMap,
  Account,
} from '@models';
import { AccountDbSaveService } from '../services/account-db-save.service';
import { AccountDbQueryService } from '../services/account-db-query.service';
import { MenuItem } from 'primeng/api';
import { AnalysisGridDialogComponent, AnalysisGridDialogParams } from '@core';
import { AccountUserFinderDialog } from './account-user-finder.dialog';
import { BudgetItemsListComponent } from '../budgets/budget-items-list.component';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AccountActivationService } from '../services/account-activation.service';

@Component({
  selector: 'prox-shipping-user-groups',
  templateUrl: './shipping-user-groups.component.html',
  standalone: false,
})
export class ShippingUserGroupsComponent extends SaveAndQueryComponent {
  @ViewChild(BudgetItemsListComponent)
  budgetListItemsComponent!: BudgetItemsListComponent;
  supplierId!: string;
  accountId!: string;
  account!: Account;
  tabViewIndex = 0;
  activeStatuses!: ActiveStatus[];
  originalActiveStatusId!: ActiveStatusEnum;

  allSugs!: ShippingUserGroup[];
  sugGridOptions!: GridOptionsExt;
  sugs: ShippingUserGroup[] = [];
  selectedSug?: ShippingUserGroup;
  userGridOptions!: GridOptionsExt;
  users: AccountUser[] = [];
  programProductTypeTags!: ProgramProductTypeTag[];
  ref!: DynamicDialogRef;

  isValidateActivated = false;

  /** The "Analyze" dropdown above the Shipping User Group grid. Every option opens the shared
   *  AnalysisGridDialogComponent, which carries the full ag-Grid Enterprise tool set. */
  analyzeItems: MenuItem[] = [
    {
      label: 'Account Users by Shipping Address',
      icon: 'pi pi-map-marker',
      command: () => this.onAnalyzeAccountUsersByShippingAddress(),
    },
  ];

  constructor(
    baseService: BaseService,
    route: ActivatedRoute,
    override dbSaveService: AccountDbSaveService,
    override dbQueryService: AccountDbQueryService,
    public pngDialogService: DialogService,
    public activationService: AccountActivationService,
    private statusService: StatusService,
  ) {
    super(baseService, route, dbSaveService, dbQueryService);
  }

  override async updateFromParams(params: object): Promise<void> {
    this.accountId = params['accountId'];
    UtilFns.assertNonEmptyString(this.accountId, 'accountId');

    const [account, activeStatuses, r4] = await Promise.all([
      this.dbQueryService.getAccountById(this.accountId),
      this.dbQueryService.getAll(ActiveStatus),

      this.dbQueryService.getShippingUserGroups(this.accountId),
    ]);
    this.account = account;
    this.activeStatuses = activeStatuses;

    this.sugGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onSugGridReady,
      onRowSelected: this.onSugRowSelected,
      rowModelType: 'clientSide',
      getRowId: (rowIdParams: GetRowIdParams) => {
        const sug = rowIdParams.data as ShippingUserGroup;
        return sug.id;
      },
    });

    this.isValidateActivated = this.activationService.hasErrors;
    if (this.isValidateActivated) {
      this.activationService.activate(this.account.id);
    }

    AgFns.captureGridRouteParams(this.sugGridOptions, this.route, 'id');
    this.userGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onUserGridReady,
      rowModelType: 'clientSide',
      getRowId: (rowIdParams: GetRowIdParams) => {
        const user = rowIdParams.data as AccountUser;
        return user.id;
      },
    });

    this.allSugs = r4;
    this.setTitle('Shipping User Groups for ' + this.account.name);
    this.isPageReady = true;
  }

  isReadOnly() {
    return this.statusService.getWorkingStatus(this.account).isActiveReadOnly;
  }

  isEditable() {
    return this.account?.activeStatusId == ActiveStatusEnum.Hold;
  }

  statusMessage() {
    let msg = '';
    if (this.account?.activeStatusId == ActiveStatusEnum.Active) {
      return 'Account is Active and Not Editable.  To edit, set Account status to Hold.';
    } else if (this.account?.activeStatusId == ActiveStatusEnum.Hold) {
      msg = 'Account is On-Hold and is Editable';
    } else {
      return 'Account is ' + this.account?.activeStatus.name + ' and is Not Editable';
    }
    return msg;
  }

  canDelete() {
    return !this.isReadOnly();
  }

  // Aug Grid

  onSugGridReady(event: GridReadyEvent) {
    const [colDefs, sortModel] = this.getSugColDefsAndSortModel();
    // this.updateSugMasterDetail(gridOptions);

    AgFns.initGrid(event, colDefs, sortModel);
    this.refreshSugGrid();

    AgFns.applyGridRouteParams(this.sugGridOptions);
  }

  getSugColDefsAndSortModel() {
    const colDefs: ColDef[] = [
      ProxAgFns.getEntityStateColDef(),
      {
        headerName: 'Name',
        field: 'name',
        editable: !this.isReadOnly(),
        filter: 'agTextColumnFilter',
        cellRenderer: 'agGroupCellRenderer',
        ...AgFns.flexWidth(250),
      },

      /*       ProxAgFns.getWorkingStatusDef('Status', this.statusService), */
      {
        ...ProxAgFns.getActiveStatusColDef('activeStatus.name'),
        ...AgFns.fixWidth(100),
      },
      ProxAgFns.getEntityStatusColDef(this.onChangeActiveStatus.bind(this), { canDisplay: this.isEditable.bind(this) }),
      ProxAgFns.getEntityDeleteColDef(this.onSugDelete.bind(this), { canDisplay: this.canDelete.bind(this) }),
    ];
    const sortModel: ISortModel = [{ colId: 'name', sort: 'asc' }];
    return [colDefs, sortModel] as const;
  }

  async onChangeActiveStatus(sug: ShippingUserGroup) {
    await this.statusService.onChangeActiveStatus(this, sug, 'Shipping User Group');
    this.sugGridOptions.apiX?.refreshCells();
  }

  onSugRowSelected(event: RowSelectedEvent) {
    if (!event.node.isSelected()) return;
    const sug = event.data as ShippingUserGroup;
    if (sug == null) return;
    this.selectedSug = sug;
    if (sug.entityAspect.entityState.isDeleted()) {
      this.selectedSug = undefined;
      this.sugGridOptions.apiX?.deselectAll();
      return;
    }

    this.updateGridRouteParams(this.sugGridOptions, sug.id);

    this.users = sug.shippingUserGroupMaps.map((x) => x.accountUser);
    AgFns.refreshGrid(this.userGridOptions, this.users);
  }

  async onSugAdd() {
    const newAug = this.dbSaveService.createEntity(ShippingUserGroup, {
      accountId: this.accountId,
      activeStatusId: ActiveStatusEnum.Hold,
    });
    newAug.entityAspect.validateEntity();
    this.allSugs.push(newAug);
    this.refreshSugGrid();
    const gridApi = this.sugGridOptions.apiX;
    if (!gridApi) return;
    const rowIndex = AgFns.getRowIndex(gridApi, newAug.id);
    this.sugGridOptions.apiX?.setFocusedCell(rowIndex ?? 0, 'name');
  }

  async onSugDelete(sug: ShippingUserGroup) {
    let allowDelete = true;
    const maps = await this.dbQueryService.getAccountUsersForShippingUserGroup(sug.id);
    if (
      maps.map((p) => {
        if (p.proximityUser.activeStatusId !== ActiveStatusEnum.Inactive) {
          allowDelete = false;
        }
      })
    )
      if (!allowDelete) {
        this.toastr.warning('Cannot delete Shipping User Group with Account Users who are not Inactive', 'Unable to Proceed');
        return;
      }

    sug.shippingUserGroupMaps.slice().forEach((x) => EntityFns.deleteOrDetach(x.entityAspect));
    sug.shippingUserGroupAddressMaps.slice().forEach((x) => EntityFns.deleteOrDetach(x.entityAspect));
    EntityFns.deleteOrDetach(sug.entityAspect);
    this.selectedSug = undefined;
    this.users = [];
    if (this.allSugs) {
      _.remove(this.allSugs, sug);
      this.refreshSugGrid();
    }
  }

  async refreshSugGrid(fullRefresh: boolean = false) {
    this.sugs = this.allSugs.slice();
    if (this.selectedSug) {
      this.selectedSug = this.sugs.includes(this.selectedSug) ? this.selectedSug : undefined;
    }
    AgFns.refreshGrid(this.sugGridOptions, this.sugs || []);
    if (fullRefresh) {
      AgFns.refreshGrid(this.sugGridOptions, this.sugs || []);
      if (this.selectedSug) {
        AgFns.selectGridRowByKey(this.sugGridOptions, (e) => e.id, this.selectedSug.id);
      } else if (this.sugs.length > 0) {
        this.selectedSug = await AgFns.selectFirstRow(this.sugGridOptions);
      }
    } else {
      if (this.selectedSug) {
        this.selectedSug = this.sugs.includes(this.selectedSug) ? this.selectedSug : undefined;
      }
    }

    if (this.selectedSug == null) {
      this.users = [];
    } else {
      this.users = this.selectedSug.shippingUserGroupMaps.map((x) => x.accountUser);
    }
    AgFns.refreshGrid(this.userGridOptions, this.users);
  }

  // User Grid

  onUserGridReady(event: GridReadyEvent) {
    const [colDefs, sortModel] = this.getUserColDefsAndSortModel();

    AgFns.initGrid(event, colDefs, sortModel);
  }

  getUserColDefsAndSortModel() {
    const colDefs: ColDef[] = [
      ProxAgFns.getEntityStateColDef(),
      {
        headerName: 'Last Name',
        field: 'proximityUser.lastName',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(150),
      },
      {
        headerName: 'First Name',
        field: 'proximityUser.firstName',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(150),
      },
      {
        headerName: 'Middle Name',
        field: 'proximityUser.middleName',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(75),
      },
      {
        headerName: 'Salutation',
        field: 'proximityUser.salutation',
        ...AgFns.fixWidth(130),
      },
      { headerName: 'User Name', field: 'proximityUser.username', filter: 'agTextColumnFilter', ...AgFns.flexWidth(150) },
      {
        headerName: 'Gender',
        field: 'gender.name',
        ...AgFns.fixWidth(130),
        filter: 'agSetColumnFilter',
      },
      {
        headerName: 'E-Mail',
        field: 'proximityUser.email',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(150),
      },
      { ...ProxAgFns.getActiveStatusColDef('proximityUser.activeStatus.name'), ...AgFns.fixWidth(100) },
      /*       ProxAgFns.getWorkingStatusDef('Status', this.statusService), */
      ProxAgFns.getEntityDeleteColDef(this.onUserDelete.bind(this), { canDisplay: this.canDelete.bind(this) }),
    ];
    const sortModel: ISortModel = [
      { colId: 'proximityUser.lastName', sort: 'asc' },
      { colId: 'proximityUser.firstName', sort: 'asc' },
    ];
    return [colDefs, sortModel] as const;
  }

  @wrapBusy
  async onUserAttach() {
    const newUsers = await this.attachUsers();
    if (newUsers.length == 0) return;
    this.users.push(...newUsers);
    AgFns.refreshGrid(this.userGridOptions, this.users);
    AgFns.updateMasterDetail(this.sugGridOptions, this.selectedSug);
  }

  async attachUsers() {
    if (this.selectedSug == null) return [];
    const sug = this.selectedSug;

    const allAccountUsers = await this.dbQueryService.getAccountUsers(this.accountId);

    const currentAccountUserIds = new Set(this.selectedSug.shippingUserGroupMaps.map((y) => y.accountUserId));
    const remainingAccountUsers = allAccountUsers.filter((au) => !currentAccountUserIds.has(au.id));
    const newAccountUsers = await this.dialogService.createFinder(AccountUserFinderDialog, {
      accountUsers: remainingAccountUsers,
      rowSelection: RowSelectionMultipleCheckbox,
    });
    if (newAccountUsers.length == 0) return [];
    newAccountUsers.forEach((user) => {
      if (!this.dbSaveService.uow.undoIfDeleted(ShippingUserGroupMap, [this.selectedSug?.id, user.id])) {
        this.dbSaveService.createEntity(ShippingUserGroupMap, {
          shippingUserGroupId: this.selectedSug?.id,
          accountUserId: user.id,
        });
      }
    });
    return newAccountUsers;
  }

  /*   async onUsersImport() {
    this.router.navigate([this.selectedSug?.id, this.selectedSug?.name], { relativeTo: this.route })
  } */

  async onUserDelete(accountUser: AccountUser) {
    if (!this.selectedSug) return;
    this.selectedSug.shippingUserGroupMaps
      .filter((x) => x.accountUserId == accountUser.id)
      .forEach((x) => {
        EntityFns.deleteOrDetach(x.entityAspect);
      });
    this.addEntitiesToValidate(this.selectedSug);
    _.remove(this.users, accountUser);
    AgFns.refreshGrid(this.userGridOptions, this.users);
  }

  // -----------------------------------------------------------------------

  //

  override async addCrossValidationErrors() {
    EntityFns.checkForDupErrors(
      this.allSugs,
      (x) => x.name,
      (e, dupName) => this.createValidationError(e, 'name', `This Proximity User Group Name is a duplicate: '${dupName}.`),
    );

    /*     const sugs = this.getEntitiesToValidate().filter(
      (x) => x instanceof ShippingUserGroup
    ) as ShippingUserGroup[]; */

    // User Groups do not need a record to be saved  Records may be imported.

    /*  sugs.forEach((s) => {
      if (
        s.activeStatusId === ActiveStatusEnum.Active &&
        s.shippingUserGroupMaps.length == 0
      ) {
        this.createValidationError(
          s,
          'name',
          `The Active Proximity User Group, '${s.name}', must have at least one Proximity User.`
        );
      }
    }); */
  }

  override async afterSave() {
    if (this.isValidateActivated) {
      if (!(await this.activationService.activate(this.account!.id))) {
        this.account!.activeStatusId = ActiveStatusEnum.Active;
        this.activationService.deactivate();
        this.isValidateActivated = false;
        await this.saveChanges();
      }
    }
    return this.afterUndo();
  }

  override async afterUndo() {
    this.allSugs = await this.dbQueryService.getShippingUserGroups(this.accountId);
    this.refreshSugGrid(true);
  }

  override async navigateToValidationError(error: EntityError) {
    const errEnt = error.entity;
    if (errEnt instanceof ShippingUserGroup) {
      AgFns.selectGridRowByKey(this.sugGridOptions, (e: ShippingUserGroup) => e.id, errEnt.id, error.propertyName);
    }
  }

  /** Analyze > Account Users by Shipping Address.
   *  One row per (shipping address, shipping user group) pair, so a group with several addresses
   *  appears once per address, and an address shared by several groups appears once per group.
   *  Live means not Inactive, so Hold and Phaseout rows are included. Users past their exit date
   *  are left out. The rows are a server DTO - the Analyze dialogs never take Breeze entities. */
  @wrapBusy
  async onAnalyzeAccountUsersByShippingAddress() {
    const rows = await this.dbQueryService.getAccountUserCountsByShippingAddress(this.accountId);
    const colDefs: ColDef[] = [
      {
        field: 'ShippingAddressName',
        headerName: 'Shipping Address Name',
        minWidth: 240,
        enableRowGroup: true,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'ShippingAddressDescription',
        headerName: 'Shipping Address Description',
        minWidth: 280,
        tooltipField: 'ShippingAddressDescription',
        filter: 'agTextColumnFilter',
      },
      {
        field: 'ShippingUserGroupName',
        headerName: 'Shipping User Group',
        minWidth: 240,
        enableRowGroup: true,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'ActiveStatusName',
        headerName: 'Group Status',
        maxWidth: 140,
        enableRowGroup: true,
        filter: 'agSetColumnFilter',
      },
      {
        field: 'AccountUserCount',
        headerName: 'Account Users',
        maxWidth: 150,
        type: 'numericColumn',
        aggFunc: 'sum',
        filter: 'agNumberColumnFilter',
      },
    ];

    const groupIds = new Set(rows.map((r) => r.ShippingUserGroupId));
    const noAddressCount = rows.filter((r) => r.ShippingAccountAddressId == null).length;

    this.pngDialogService.open(AnalysisGridDialogComponent, {
      header: `Account Users by Shipping Address — ${this.account.name}`,
      width: '85vw',
      height: '75vh',
      contentStyle: { overflow: 'hidden' },
      closable: true,
      modal: true,
      resizable: true,
      maximizable: true,
      data: {
        title: 'Account Users by Shipping Address',
        subtitle:
          `${rows.length} address/group pair(s) across ${groupIds.size} active group(s). ` +
          'Inactive groups, inactive users and users past their exit date are left out. ' +
          'The count is the group membership, so a sum across rows counts a user once per address their group serves.' +
          (noAddressCount > 0 ? ` ${noAddressCount} group(s) have no address applied — their address cells are empty.` : ''),
        rowData: rows,
        columnDefs: colDefs,
        exportFileName: 'account-users-by-shipping-address',
      } as AnalysisGridDialogParams,
    });
  }
}
